// Google-Sheets-style click-to-highlight for Urban Unity's sheet views (Tab for Joey Forslund, Oct 29 2026, 6:55 PM ET:
// "I want to be able to highlight the column when I click on the number on the left" + his Google Sheets screenshot, where
// clicking row number 20 lit up that whole row). Wrap any area holding one or more sheet tables:
//  - click a row's # cell (far left)  → that whole row gets a light band + border across every column;
//  - click a column header (the date / webinar) → that column is highlighted top to bottom;
//  - click the same one again, click the # header, or press Esc → cleared; clicking another row / column moves it.
// Works by delegation + one scoped stylesheet (no change to the tables' own markup). Theme-aware: Sheets blue on the colour
// themes, black / grey on the black & white (html.noir) theme. Also exports the All / Organic / Paid toggle, its shared selection and the sheet title.
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

type Sel = { table: number; kind: "row" | "col"; index: number } | null;

export function SheetHighlight({ children, resetKey, className, style }: { children: ReactNode; resetKey?: unknown; className?: string; style?: CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  const uid = `shl${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const [sel, setSel] = useState<Sel>(null);
  useEffect(() => { setSel(null); }, [resetKey]);
  useEffect(() => {
    if (!sel) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setSel(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel]);
  const tables = () => Array.from(ref.current?.querySelectorAll("table") ?? []);
  // Number the tables every render so the stylesheet can address the one clicked.
  useEffect(() => { tables().forEach((t, i) => t.setAttribute("data-shl-t", String(i))); });

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const cell = target.closest("td,th") as HTMLTableCellElement | null;
    if (!cell || !ref.current?.contains(cell)) return;
    const table = cell.closest("table"); if (!table) return;
    const t = tables().indexOf(table); if (t < 0) return;
    table.setAttribute("data-shl-t", String(t));
    const tr = cell.parentElement as HTMLTableRowElement | null;
    const group = tr?.parentElement;
    if (!tr || !group) return;
    if (target.closest("button,a,input,select,textarea,[role=button]")) return;
    if (group.tagName === "TBODY") {
      // Only a real metric row's # cell (section bands span the table and are not rows to pick).
      if (cell.cellIndex !== 0 || cell.colSpan > 1 || tr.cells.length < 3 || !(cell.textContent || "").trim()) return;
      const index = Array.prototype.indexOf.call(group.children, tr) + 1;
      setSel((s) => (s && s.table === t && s.kind === "row" && s.index === index ? null : { table: t, kind: "row", index }));
      return;
    }
    if (group.tagName === "THEAD") {
      if (cell.colSpan > 1) return;
      let col = 0; for (let i = 0; i < cell.cellIndex; i++) col += tr.cells[i]!.colSpan || 1;
      if (col === 0) { setSel(null); return; }
      if (col === 1) return; // the Metric column
      const index = col + 1;
      setSel((s) => (s && s.table === t && s.kind === "col" && s.index === index ? null : { table: t, kind: "col", index }));
    }
  };

  const T = sel ? `#${uid} table[data-shl-t="${sel.table}"]` : "";
  const css = `
#${uid} { --shl-bg: #e8f0fe; --shl-head: #d3e3fd; --shl-line: #1a73e8; --shl-head-text: #ffffff; }
html.noir #${uid} { --shl-bg: #ececec; --shl-head: #d4d4d4; --shl-line: #000000; --shl-head-text: #ffffff; }
#${uid} table > tbody > tr > td:first-child { cursor: pointer; }
#${uid} table > thead > tr > th:nth-child(n+3) { cursor: pointer; }
${sel?.kind === "row" ? `
${T} > tbody > tr:nth-child(${sel.index}) > td { background: var(--shl-bg) !important; box-shadow: inset 0 2px 0 var(--shl-line), inset 0 -2px 0 var(--shl-line) !important; }
${T} > tbody > tr:nth-child(${sel.index}) > td:first-child { background: var(--shl-head) !important; color: var(--shl-line) !important; font-weight: 700 !important; box-shadow: inset 2px 0 0 var(--shl-line), inset 0 2px 0 var(--shl-line), inset 0 -2px 0 var(--shl-line) !important; }
${T} > tbody > tr:nth-child(${sel.index}) > td:last-child { box-shadow: inset -2px 0 0 var(--shl-line), inset 0 2px 0 var(--shl-line), inset 0 -2px 0 var(--shl-line) !important; }` : ""}
${sel?.kind === "col" ? `
${T} > tbody > tr > td:nth-child(${sel.index}):not([colspan]) { background: var(--shl-bg) !important; box-shadow: inset 2px 0 0 var(--shl-line), inset -2px 0 0 var(--shl-line) !important; }
${T} > thead > tr > th:nth-child(${sel.index}) { background: var(--shl-line) !important; color: var(--shl-head-text) !important; box-shadow: inset 0 0 0 2px var(--shl-line) !important; }
${T} > thead > tr > th:nth-child(${sel.index}) * { color: var(--shl-head-text) !important; }` : ""}
`;
  return (
    <div id={uid} ref={ref} onClick={onClick} className={className} style={style}>
      <style>{css}</style>
      {children}
    </div>
  );
}

export type TrafficKey = "all" | "organic" | "paid";
// One velocity sheet switched by All / Organic / Paid (Tab for Joey Forslund, Oct 29 2026, 7:30 PM ET: "whenever I click on
// either All or Organic or Paid, it will adjust the metrics on the velocity metrics view (so I don't have to scroll down …)
// it's just one sheet that adjusts depending on what's selected"). The stacked Blended / Organic / Paid sections are gone.
export const TRAFFIC_SECTIONS: Array<{ key: TrafficKey; label: string }> = [
  { key: "all", label: "Blended" },
  { key: "organic", label: "Organic" },
  { key: "paid", label: "Paid" },
];
/** Sheet / export label for a traffic selection. */
export const TRAFFIC_LABEL: Record<TrafficKey, string> = { all: "Blended (all traffic)", organic: "Organic", paid: "Paid (Meta ads)" };
const TRAFFIC_NOTE: Record<TrafficKey, string> = {
  all: "Blended = all traffic, paid and organic together.",
  paid: "",
  organic: "",
};
export const UU_PAID_RULE_TEXT =
  "Paid vs Organic = each person's MOST RECENT opt-in: Paid when it came from a Meta ad link (Meta campaign / ad set / ad name or ad id, " +
  "utm_source fb / ads / paid, utm_medium paid / cpc / \"Ads …\"); Organic for every other link (IG bio / story / reels, ManyChat / " +
  "chatwithmarsh DMs, email, SMS, WhatsApp, YouTube, TikTok, blank). Someone who once came through an ad and later opted in organically is " +
  "Organic. Applications, bookings, calls, closes and cash follow the person's latest opt-in before that event. Paid + Organic = Blended for every count.";

// The selection is shared by the three velocity views (Webinars → List, Book a call → Sheets, Dashboard → Velocity Metrics)
// for this browser tab: pick Paid on one and the others open on Paid too.
const TRAFFIC_STORE = "uu-velocity-traffic";
const TRAFFIC_EVENT = "uu-velocity-traffic";
function readTraffic(): TrafficKey {
  try { const v = window.sessionStorage.getItem(TRAFFIC_STORE); return v === "organic" || v === "paid" ? v : "all"; } catch { return "all"; }
}
export function useTrafficSelection(): [TrafficKey, (next: TrafficKey) => void] {
  const [traffic, setTrafficState] = useState<TrafficKey>(readTraffic);
  useEffect(() => {
    const on = (e: Event) => { const v = (e as CustomEvent<TrafficKey>).detail; if (v === "all" || v === "organic" || v === "paid") setTrafficState(v); };
    window.addEventListener(TRAFFIC_EVENT, on);
    return () => window.removeEventListener(TRAFFIC_EVENT, on);
  }, []);
  const setTraffic = useCallback((next: TrafficKey) => {
    setTrafficState(next);
    try { window.sessionStorage.setItem(TRAFFIC_STORE, next); } catch { /* private mode */ }
    window.dispatchEvent(new CustomEvent(TRAFFIC_EVENT, { detail: next }));
  }, []);
  return [traffic, setTraffic];
}

/** The Webinars tab's "Split by traffic: All / Organic / Paid" control, same look, for the views that had none. */
export function TrafficToggle({ value, onChange, label = "Split by traffic:" }: { value: TrafficKey; onChange: (next: TrafficKey) => void; label?: string }) {
  return (
    <div className="flex items-center gap-1.5" data-traffic-toggle>
      <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
      <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5" role="group" aria-label="Split by traffic">
        {(["all", "organic", "paid"] as const).map((t) => (
          <button key={t} type="button" onClick={() => onChange(t)} aria-pressed={value === t}
            className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
              value === t ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
            }`}>{t}</button>
        ))}
      </div>
    </div>
  );
}

/** "Blended / Organic / Paid" heading for the sheet on show, with a small (i) that explains the rule. */
export function TrafficSectionTitle({ traffic, extra, loading, className }: { traffic: TrafficKey; extra?: string; loading?: boolean; className?: string }) {
  const label = TRAFFIC_LABEL[traffic] ?? traffic;
  const text = [traffic === "all" ? `${TRAFFIC_NOTE.all} ${UU_PAID_RULE_TEXT}` : traffic === "paid" ? `Paid only. ${UU_PAID_RULE_TEXT}` : `Organic only. ${UU_PAID_RULE_TEXT}`, extra].filter(Boolean).join(" ");
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const show = () => { const r = btn.current?.getBoundingClientRect(); if (r) setTip({ x: Math.min(r.left, window.innerWidth - 336), y: r.bottom + 6 }); };
  return (
    <div className={className ?? "flex items-center gap-2 mt-2 mb-2"} data-traffic-title={traffic}>
      <h3 className="text-sm font-bold tracking-wide uppercase" style={{ color: "var(--foreground, inherit)" }}>{label}</h3>
      <button ref={btn} type="button" aria-label={`What ${label} means`} onMouseEnter={show} onMouseLeave={() => setTip(null)} onFocus={show} onBlur={() => setTip(null)}
        onClick={(e) => { e.stopPropagation(); tip ? setTip(null) : show(); }}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full border text-[10px] font-semibold text-muted-foreground hover:text-foreground"
        style={{ lineHeight: 1 }}>i</button>
      {loading && <span className="text-xs text-muted-foreground">loading…</span>}
      {tip && createPortal(
        <div role="tooltip" style={{ position: "fixed", left: Math.max(8, tip.x), top: tip.y, zIndex: 120, maxWidth: 320 }}
          className="rounded-md border bg-popover text-popover-foreground shadow-md px-3 py-2 text-xs leading-snug">{text}</div>,
        document.body,
      )}
    </div>
  );
}
